import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useTranslation } from 'react-i18next';
import {
  getPendingMutations,
  getPendingCount,
  removeMutation,
  incrementRetryCount,
  QueuedMutation,
} from '@/lib/offlineQueue';

const MAX_RETRIES = 3;

export function useOfflineSync() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const { isOnline } = useNetworkStatus();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const syncInProgress = useRef(false);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    const count = await getPendingCount();
    setPendingCount(count);
  }, []);

  // Listen for queue changes
  useEffect(() => {
    updatePendingCount();
    
    const handleQueueChange = () => {
      updatePendingCount();
    };
    
    window.addEventListener('offline-queue-change', handleQueueChange);
    return () => {
      window.removeEventListener('offline-queue-change', handleQueueChange);
    };
  }, [updatePendingCount]);

  // Process a single mutation
  const processMutation = useCallback(async (mutation: QueuedMutation): Promise<boolean> => {
    try {
      const { type, action, payload } = mutation;

      if (type === 'transaction') {
        if (action === 'create') {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { tag_ids, ...transactionData } = payload;
          const { error } = await supabase.from('transactions').insert(transactionData as {
            user_id: string;
            type: string;
            amount: number;
            description?: string;
            category_id?: string;
            date?: string;
          });
          if (error) throw error;
          
          // Handle tags if present
          if (tag_ids && Array.isArray(tag_ids) && payload.id) {
            const tagInserts = (tag_ids as string[]).map(tag_id => ({
              transaction_id: payload.id as string,
              tag_id,
            }));
            await supabase.from('transaction_tags').insert(tagInserts);
          }
        } else if (action === 'update') {
          const id = payload.id as string;
          const tagIds = payload.tag_ids as string[] | undefined;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id: _, tag_ids: __, ...updateData } = payload;
          
          const { error } = await supabase
            .from('transactions')
            .update(updateData)
            .eq('id', id);
          if (error) throw error;
          
          // Handle tags
          if (tagIds !== undefined) {
            const { data: existingTags } = await supabase
              .from('transaction_tags')
              .select('id, tag_id')
              .eq('transaction_id', id);

            const existingTagIds = existingTags?.map(t => t.tag_id) || [];
            const tagsToRemove = existingTags?.filter(t => !tagIds.includes(t.tag_id)) || [];
            const tagsToAdd = tagIds.filter(tagId => !existingTagIds.includes(tagId));

            if (tagsToRemove.length > 0) {
              await supabase
                .from('transaction_tags')
                .delete()
                .in('id', tagsToRemove.map(t => t.id));
            }

            if (tagsToAdd.length > 0) {
              await supabase
                .from('transaction_tags')
                .insert(tagsToAdd.map(tag_id => ({ transaction_id: id, tag_id })));
            }
          }
        } else if (action === 'delete') {
          const { error } = await supabase
            .from('transactions')
            .delete()
            .eq('id', payload.id as string);
          if (error) throw error;
        }
      } else if (type === 'category') {
        if (action === 'create') {
          const { error } = await supabase.from('categories').insert(payload as {
            user_id: string;
            name: string;
            icon?: string;
            color?: string;
            type?: string;
            parent_id?: string;
          });
          if (error) throw error;
        } else if (action === 'update') {
          const id = payload.id as string;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id: _, ...updateData } = payload;
          const { error } = await supabase.from('categories').update(updateData as Record<string, unknown>).eq('id', id);
          if (error) throw error;
        } else if (action === 'delete') {
          const { error } = await supabase
            .from('categories')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', payload.id as string);
          if (error) throw error;
        }
      } else if (type === 'tag') {
        if (action === 'create') {
          const { error } = await supabase.from('tags').insert(payload as {
            user_id: string;
            name: string;
            color?: string;
          });
          if (error) throw error;
        } else if (action === 'update') {
          const id = payload.id as string;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id: _, ...updateData } = payload;
          const { error } = await supabase.from('tags').update(updateData as Record<string, unknown>).eq('id', id);
          if (error) throw error;
        } else if (action === 'delete') {
          const { error } = await supabase.from('tags').delete().eq('id', payload.id as string);
          if (error) throw error;
        }
      } else if (type === 'budget') {
        if (action === 'create') {
          const { error } = await supabase.from('budgets').insert(payload as {
            user_id: string;
            amount: number;
            year: number;
            category_id?: string;
            month?: number;
            period_type?: string;
          });
          if (error) throw error;
        } else if (action === 'update') {
          const id = payload.id as string;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id: _, ...updateData } = payload;
          const { error } = await supabase.from('budgets').update(updateData as Record<string, unknown>).eq('id', id);
          if (error) throw error;
        } else if (action === 'delete') {
          const { error } = await supabase.from('budgets').delete().eq('id', payload.id as string);
          if (error) throw error;
        }
      } else if (type === 'profile') {
        if (action === 'update') {
          const userId = payload.user_id as string;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { user_id: _, ...updateData } = payload;
          const { error } = await supabase.from('profiles').update(updateData).eq('user_id', userId);
          if (error) throw error;
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to process mutation:', mutation.id, error);
      return false;
    }
  }, []);

  // Sync all pending mutations
  const syncPendingMutations = useCallback(async () => {
    if (!isOnline || syncInProgress.current) return;
    
    syncInProgress.current = true;
    setIsSyncing(true);
    
    try {
      const mutations = await getPendingMutations();
      
      if (mutations.length === 0) {
        setIsSyncing(false);
        syncInProgress.current = false;
        return;
      }

      let successCount = 0;
      let failedCount = 0;

      for (const mutation of mutations) {
        if (mutation.retryCount >= MAX_RETRIES) {
          // Too many retries, remove from queue
          await removeMutation(mutation.id);
          failedCount++;
          continue;
        }

        const success = await processMutation(mutation);
        
        if (success) {
          await removeMutation(mutation.id);
          successCount++;
        } else {
          await incrementRetryCount(mutation.id);
          failedCount++;
        }
      }

      // Refresh data after sync
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });

      if (successCount > 0) {
        toast({
          title: t('sync.synced'),
          description: t('sync.syncedCount', { count: successCount }),
        });
      }

      if (failedCount > 0) {
        toast({
          title: t('sync.syncFailed'),
          description: t('sync.failedCount', { count: failedCount }),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
      syncInProgress.current = false;
      await updatePendingCount();
    }
  }, [isOnline, processMutation, queryClient, toast, t, updatePendingCount]);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      syncPendingMutations();
    }
  }, [isOnline, pendingCount, syncPendingMutations]);

  return {
    pendingCount,
    isSyncing,
    isOnline,
    syncPendingMutations,
  };
}
